import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  Percent,
  RefreshCw,
  Save,
  ShieldCheck,
  TrendingDown,
  Zap,
} from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthConfig() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

export default function StripeConnectTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [feeInfo, setFeeInfo] = useState(null);
  // Structured platform-side error (e.g. platform_profile_incomplete).
  // Stored separately from generic toasts so we can render an actionable
  // inline alert with a real clickable button instead of a transient toast
  // (the user can't click a link inside a Sonner toast).
  const [onboardError, setOnboardError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/stripe/connect/status`, getAuthConfig());
      setStatus(res.data);
    } catch {
      toast.error("Failed to load Stripe Connect status");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/stripe/connect/settings`, getAuthConfig());
      setFeeInfo(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchSettings();
  }, [fetchStatus, fetchSettings]);

  const startOnboarding = async () => {
    setOnboarding(true);
    setOnboardError(null);
    try {
      const origin = window.location.origin;
      const res = await axios.post(
        `${API}/api/stripe/connect/onboard`,
        {
          return_url: `${origin}/dashboard/settings?tab=stripe-connect&connect=return`,
          refresh_url: `${origin}/dashboard/settings?tab=stripe-connect&connect=refresh`,
        },
        getAuthConfig()
      );
      window.location.href = res.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns a structured object for known platform-side errors
      // (platform_profile_incomplete, connect_not_enabled, ...). Surface it
      // inline with a clickable action button. Fall back to a toast for
      // generic / unstructured errors.
      if (detail && typeof detail === "object" && detail.error_code) {
        setOnboardError(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to start onboarding");
      }
      setOnboarding(false);
    }
  };

  const openDashboard = async () => {
    try {
      const res = await axios.get(`${API}/api/stripe/connect/dashboard-link`, getAuthConfig());
      window.open(res.data.url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open Stripe dashboard");
    }
  };

  const startMigration = async () => {
    if (!window.confirm(
      "Switching creates a fresh Stripe account (you'll re-enter your bank and identity details — takes ~5 minutes). " +
      "Your current account keeps working and pending payouts complete normally. Continue?"
    )) return;
    setMigrating(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(
        `${API}/api/stripe/connect/migrate-fee-model`,
        {
          return_url: `${origin}/dashboard/settings?tab=stripe-connect&connect=return`,
          refresh_url: `${origin}/dashboard/settings?tab=stripe-connect&connect=refresh`,
        },
        getAuthConfig()
      );
      window.location.href = res.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to start the fee-structure switch");
      setMigrating(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Stripe account? You will no longer be able to accept payments until you reconnect.")) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/api/stripe/connect/disconnect`, {}, getAuthConfig());
      toast.success("Stripe account disconnected");
      setStatus({ connected: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-theme" data-testid="stripe-connect-loading">
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const connected = status?.connected;
  const chargesEnabled = status?.charges_enabled;
  const payoutsEnabled = status?.payouts_enabled;
  const onboardingComplete = status?.onboarding_complete;
  const currentlyDue = status?.requirements?.currently_due || [];
  const pastDue = status?.requirements?.past_due || [];
  const disabledReason = status?.disabled_reason;

  // Not connected state
  if (!connected) {
    return (
      <Card className="glass-card border-theme" data-testid="stripe-connect-tab">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap className="w-5 h-5 text-[#635BFF]" />
            Stripe Connect
          </CardTitle>
          <CardDescription>
            Connect your Stripe account to accept payments directly from your customers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {onboardError && (
            <div
              className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3"
              data-testid="stripe-connect-action-required"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-semibold text-amber-600">
                    One more step on Stripe's side
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {onboardError.message}
                  </p>
                </div>
                <button
                  onClick={() => setOnboardError(null)}
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  aria-label="Dismiss"
                  data-testid="stripe-connect-error-dismiss"
                >
                  ×
                </button>
              </div>
              {onboardError.action_url && (
                <div className="flex flex-col sm:flex-row gap-2 pl-8">
                  <a
                    href={onboardError.action_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stripe-connect-action-link"
                  >
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white gap-2 w-full sm:w-auto">
                      <ExternalLink className="w-4 h-4" />
                      {onboardError.action_label || "Open Stripe Dashboard"}
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    onClick={() => { setOnboardError(null); startOnboarding(); }}
                    disabled={onboarding}
                    className="gap-2 w-full sm:w-auto"
                    data-testid="stripe-connect-retry-btn"
                  >
                    {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Try connecting again
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="rounded-xl border border-dashed border-border p-8 text-center space-y-4" data-testid="stripe-connect-empty-state">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[#635BFF]/10 flex items-center justify-center">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none">
                <rect width="40" height="40" rx="8" fill="#635BFF"/>
                <path d="M18.5 15.5c0-1.38 1.12-1.93 2.97-1.93 2.66 0 6.02 .81 8.68 2.24V9.24C27.56 8.08 25.02 7.5 22.47 7.5c-5.58 0-9.3 2.91-9.3 7.78 0 7.58 10.44 6.37 10.44 9.64 0 1.63-1.42 2.16-3.4 2.16-2.95 0-6.7-1.21-9.68-2.84v6.71c3.3 1.42 6.62 2.03 9.68 2.03 5.72 0 9.64-2.83 9.64-7.77C29.85 17.68 18.5 19.14 18.5 15.5z" fill="white"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-base">Connect your Stripe account</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Set up Stripe Express to accept credit card payments, ACH transfers, and more directly from your customers. Funds are deposited straight to your bank account.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button
                onClick={startOnboarding}
                disabled={onboarding}
                className="bg-[#635BFF] hover:bg-[#5349E0] text-white gap-2"
                data-testid="stripe-connect-start-btn"
              >
                {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {onboarding ? "Redirecting..." : "Connect with Stripe"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              You'll be redirected to Stripe to securely set up your account. Takes ~5 minutes.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Connected state
  return (
    <Card className="glass-card border-theme" data-testid="stripe-connect-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-[#635BFF]" />
              Stripe Connect
            </CardTitle>
            <CardDescription>
              Manage your connected Stripe Express account.
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={fetchStatus} data-testid="stripe-connect-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Verification-in-progress fallback (migrated accounts) */}
        {status?.fallback_active && (
          <div className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-4 flex items-start gap-3" data-testid="stripe-fallback-banner">
            <ShieldCheck className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-sky-600">
                Your new account is being verified by Stripe
              </p>
              <p className="text-sm text-muted-foreground">
                No action needed — payments continue through your previous account at your current
                rate until verification completes, then switch to the 1% fee structure automatically.
                Verification usually takes minutes but can take 1–2 business days. If Stripe needs
                anything from you, it will appear in your Stripe Dashboard.
              </p>
            </div>
          </div>
        )}

        {/* New fee structure migration (legacy platform-pays accounts only) */}
        {status?.needs_fee_migration && (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3" data-testid="stripe-fee-migration-banner">
            <div className="flex items-start gap-3">
              <TrendingDown className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold text-emerald-600">
                  New lower platform fee available — 1%
                </p>
                <p className="text-sm text-muted-foreground">
                  Switch to our new fee structure: Fieldz AI's fee drops to just 1% per transaction.
                  Card processing fees are then billed by Stripe directly to your account at Stripe's
                  standard rates (2.9% + 30¢), and you get access to the full Stripe Dashboard.
                </p>
                <p className="text-xs text-muted-foreground">
                  Stripe requires a fresh account for this change, so you'll re-enter your bank and
                  identity details (~5 min). Your current account keeps working until you finish.
                </p>
              </div>
            </div>
            <div className="pl-8">
              <Button
                size="sm"
                onClick={startMigration}
                disabled={migrating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="stripe-fee-migration-btn"
              >
                {migrating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {migrating ? "Redirecting..." : "Switch to 1% fee structure"}
              </Button>
            </div>
          </div>
        )}

        {/* Status overview */}
        <div className="rounded-xl border border-border p-5 space-y-4" data-testid="stripe-connect-status">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Account Status</span>
            {onboardingComplete ? (
              <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1" data-testid="stripe-status-active">
                <CheckCircle2 className="w-3 h-3" /> Active
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-400 gap-1" data-testid="stripe-status-pending">
                <AlertCircle className="w-3 h-3" /> Action Required
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatusPill
              label="Card Payments"
              enabled={chargesEnabled}
              testId="stripe-charges-status"
            />
            <StatusPill
              label="Payouts"
              enabled={payoutsEnabled}
              testId="stripe-payouts-status"
            />
            <StatusPill
              label="Onboarding"
              enabled={onboardingComplete}
              testId="stripe-onboarding-status"
            />
          </div>

          {status?.account_id && (
            <p className="text-xs text-muted-foreground font-mono">
              Account: {status.account_id}
            </p>
          )}
        </div>

        {/* Requirements warning */}
        {(currentlyDue.length > 0 || pastDue.length > 0) && (
          <div className="rounded-xl border border-amber-400/50 bg-amber-500/5 p-4 space-y-3" data-testid="stripe-connect-requirements">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                {pastDue.length > 0 ? "Past-due requirements" : "Pending requirements"}
              </span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 pl-6 list-disc">
              {[...pastDue, ...currentlyDue].map((req) => (
                <li key={req}>{formatRequirement(req)}</li>
              ))}
            </ul>
            <Button
              size="sm"
              onClick={startOnboarding}
              disabled={onboarding}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-1"
              data-testid="stripe-connect-complete-btn"
            >
              {onboarding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              Complete Requirements
            </Button>
          </div>
        )}

        {/* Disabled reason */}
        {disabledReason && (
          <div className="rounded-xl border border-red-400/50 bg-red-500/5 p-4" data-testid="stripe-connect-disabled">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Account Disabled</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{disabledReason}</p>
          </div>
        )}

        {/* Platform Processing Fee — read-only, managed by Fieldz AI */}
        {onboardingComplete && (
          <div className="rounded-xl border border-border p-5 space-y-2" data-testid="stripe-connect-fee-settings">
            <div className="flex items-center gap-2">
              <Percent className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Platform Processing Fee</span>
            </div>
            <p className="text-lg font-semibold text-foreground" data-testid="stripe-connect-fee-display">
              {feeInfo
                ? `${feeInfo.platform_fee_percent}% + $${Number(feeInfo.platform_fee_flat ?? 0).toFixed(2)}`
                : "—"}
              <span className="text-xs font-normal text-muted-foreground ml-2">per successful transaction</span>
            </p>
            <p className="text-xs text-muted-foreground">
              {feeInfo?.fee_model === "merchant_pays"
                ? "Fieldz AI's platform fee, deducted from each customer payment. Stripe's card processing fees are billed by Stripe directly to your account at Stripe's standard rates."
                : "Deducted automatically from each customer payment before payout to your Stripe account. This fee is set by Fieldz AI and already covers card processing — Stripe does not charge you separately."}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          {onboardingComplete && (
            <Button
              variant="outline"
              onClick={openDashboard}
              className="gap-2"
              data-testid="stripe-connect-dashboard-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Stripe Dashboard
            </Button>
          )}
          {!onboardingComplete && (
            <Button
              onClick={startOnboarding}
              disabled={onboarding}
              className="bg-[#635BFF] hover:bg-[#5349E0] text-white gap-2"
              data-testid="stripe-connect-resume-btn"
            >
              {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {onboarding ? "Redirecting..." : "Resume Onboarding"}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={disconnect}
            disabled={disconnecting}
            className="text-red-500 hover:text-red-600 hover:bg-red-500/10 gap-2"
            data-testid="stripe-connect-disconnect-btn"
          >
            {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2Off className="w-4 h-4" />}
            Disconnect
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function StatusPill({ label, enabled, testId }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 flex items-center gap-2 text-sm ${
        enabled
          ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600"
          : "border-border bg-muted/30 text-muted-foreground"
      }`}
      data-testid={testId}
    >
      {enabled ? (
        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
      ) : (
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
      )}
      {label}
    </div>
  );
}


function formatRequirement(req) {
  const map = {
    "external_account": "Bank account or debit card",
    "business_profile.url": "Business website URL",
    "business_profile.mcc": "Business category",
    "individual.first_name": "Legal first name",
    "individual.last_name": "Legal last name",
    "individual.dob.day": "Date of birth",
    "individual.dob.month": "Date of birth",
    "individual.dob.year": "Date of birth",
    "individual.address.line1": "Personal address",
    "individual.address.city": "City",
    "individual.address.state": "State",
    "individual.address.postal_code": "Postal code",
    "individual.ssn_last_4": "Last 4 of SSN",
    "individual.id_number": "Tax ID / SSN",
    "tos_acceptance.date": "Terms of Service acceptance",
    "tos_acceptance.ip": "Terms of Service acceptance",
    "individual.verification.document": "Identity document",
  };
  return map[req] || req.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
